import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Package, Loader2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@forgenetwork/ui';
import { RepositoryCard, RepositoryUpload, type Repository } from '@/components/repository';
import { api } from '@/lib/api';
import { useToast } from '@/shared/hooks/useToast';
import { useWorkspaces } from '@/features/workspace/api';

export const Route = createFileRoute('/_authenticated/repositories/')({
  component: RepositoriesPage,
});

function RepositoriesPage(): React.JSX.Element {
  const [showUpload, setShowUpload] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get user's workspaces to find the active one
  const { data: workspacesData } = useWorkspaces();
  const workspaces = React.useMemo(() => {
    if (!workspacesData) return [];
    if (Array.isArray(workspacesData)) return workspacesData;
    if (workspacesData && typeof workspacesData === 'object' && 'workspaces' in workspacesData) {
      const ws = (workspacesData as { workspaces?: unknown }).workspaces;
      return Array.isArray(ws) ? ws : [];
    }
    return [];
  }, [workspacesData]);

  // Use first workspace as default (could be enhanced with workspace selector)
  const workspaceId = workspaces[0]?.id;

  const {
    data: repositoriesData,
    isLoading,
    error,
  } = useQuery<{ data: Repository[] }>({
    queryKey: ['repositories', workspaceId],
    queryFn: async () => {
      if (!workspaceId) throw new Error('No workspace available');
      return await api.get<{ data: Repository[] }>(`/workspaces/${workspaceId}/repositories`);
    },
    enabled: !!workspaceId,
  });

  const repositories = repositoriesData?.data;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await api.delete(`/repositories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repositories', workspaceId] });
      toast({
        title: 'Success',
        description: 'Repository deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete repository',
        variant: 'destructive',
      });
    },
  });

  const handleUploadSuccess = (): void => {
    setShowUpload(false);
    queryClient.invalidateQueries({ queryKey: ['repositories', workspaceId] });
    toast({
      title: 'Success',
      description: 'Repository uploaded successfully',
    });
  };

  const handleUploadError = (error: string): void => {
    toast({
      title: 'Upload Failed',
      description: error,
      variant: 'destructive',
    });
  };

  const handleDelete = (id: string): void => {
    if (confirm('Are you sure you want to delete this repository?')) {
      deleteMutation.mutate(id);
    }
  };

  // Show loading while fetching workspaces or repositories
  if (!workspaceId || isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Failed to load repositories'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Repositories</h1>
            <p className="text-muted-foreground mt-1">
              Manage and analyze your code repositories
            </p>
          </div>
          <Button onClick={() => setShowUpload(!showUpload)}>
            <Plus className="h-4 w-4 mr-2" />
            Upload Repository
          </Button>
        </div>

        {showUpload && (
          <RepositoryUpload
            workspaceId={workspaceId}
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
          />
        )}

        {!repositories || repositories.length === 0 ? (
          <Card>
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <Package className="h-16 w-16 text-muted-foreground" />
              </div>
              <CardTitle>No repositories yet</CardTitle>
              <CardDescription>
                Upload your first repository to get started with code analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center pb-8">
              <Button onClick={() => setShowUpload(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Upload Repository
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {repositories.map((repo) => (
              <RepositoryCard
                key={repo.id}
                repository={repo}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
